import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Settings, Trophy, Link2, MoreHorizontal, Ban, MailWarning, Shield, Share2, Play, Images, Pin, Lock, Sparkles } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Avatar, Spinner, Empty, BackButton, DemoChip } from "@/components/Common";
import { BussaButton, FollowButton } from "@/components/Bussa";
import { MessageButton } from "@/pages/Messaggi";

const VIDEO_FIRST = ["musica", "danza", "comicita", "rap", "recitazione", "freestyle", "gaming"];
const IMAGE_FIRST = ["fotografia", "arte", "moda", "cucina"];

function VerifyBanner() {
  const [sent, setSent] = useState(false);
  const resend = async () => { try { await api.post("/auth/resend-verification"); setSent(true); toast.success("Email inviata"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mx-5 md:mx-10 mb-4 surface p-4 flex items-center gap-3 text-sm" data-testid="verify-email-banner">
      <MailWarning className="text-primary shrink-0" size={18} />
      <span className="flex-1 text-ink/84">Conferma la tua email per far contare i tuoi voti nelle Daily.</span>
      <button className="btn btn-ghost px-3 py-1.5 text-xs" disabled={sent} onClick={resend} data-testid="resend-verification-btn">{sent ? "Inviata" : "Invia di nuovo"}</button>
    </div>
  );
}

const src = (c: any) => { const m = c.media?.[0]; return m ? mediaUrl(m.kind === "video" ? m.thumb_url : m.url) : undefined; };
const fmtDur = (d?: number) => (d ? `${Math.floor(d / 60)}:${String(Math.round(d % 60)).padStart(2, "0")}` : "");

function Tile({ c, className = "", natural = false }: any) {
  const [loaded, setLoaded] = useState(false);
  const m = c.media?.[0];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-white/[.04] group ${className}`}>
      <Link to={`/c/${c.id}`} data-testid="profile-tile" className="block w-full h-full">
        {!loaded && <div className={`skeleton ${natural ? "aspect-[4/5]" : "absolute inset-0"}`} />}
        {src(c) && <img src={src(c)} alt={c.caption || "Contenuto"} loading="lazy" onLoad={() => setLoaded(true)}
          className={`${natural ? "w-full h-auto" : "w-full h-full object-cover"} transition-[transform,opacity] duration-500 group-hover:scale-[1.03] ${loaded ? "opacity-100" : "opacity-0"}`} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-80" />
        <div className="absolute top-2 right-2 flex gap-1.5 text-white drop-shadow">
          {c.pinned && <Pin size={14} fill="currentColor" />}
          {c.type === "gallery" && <Images size={14} />}
          {c.visibility === "connections" && <Lock size={14} />}
        </div>
        {c.type === "video" && <div className="absolute bottom-2 left-2 chip bg-black/45 border-white/20 text-white py-0.5"><Play size={10} fill="currentColor" />{fmtDur(m?.duration)}</div>}
      </Link>
    </div>
  );
}

function Works({ items, niche }: { items: any[]; niche?: string }) {
  if (!items.length) return <Empty testId="profile-empty">Nessun contenuto pubblicato.</Empty>;
  if (IMAGE_FIRST.includes(niche || "")) {
    return <div className="columns-2 md:columns-3 gap-2 [&>*]:mb-2" data-testid="profile-contents-grid" data-layout="masonry">{items.map((c) => <div key={c.id} className="break-inside-avoid"><Tile c={c} natural /></div>)}</div>;
  }
  if (VIDEO_FIRST.includes(niche || "")) {
    return <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="profile-contents-grid" data-layout="reel">{items.map((c) => <Tile key={c.id} c={c} className="aspect-[9/14]" />)}</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid="profile-contents-grid" data-layout="grid">
      {items.map((c, i) => <Tile key={c.id} c={c} className={i === 0 && items.length > 4 ? "col-span-2 row-span-2" : "aspect-[4/5]"} />)}
    </div>
  );
}

function Featured({ items }: any) {
  if (!items.length) return null;
  return (
    <section className="space-y-3" data-testid="portfolio-strip">
      <h2 className="label text-ink/60 flex items-center gap-2"><Sparkles size={12} className="text-[hsl(var(--violet))]" />In evidenza</h2>
      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 snap-x">
        {items.map((c: any) => <Tile key={c.id} c={c} className="w-40 md:w-52 aspect-[3/4] shrink-0 snap-start" />)}
      </div>
    </section>
  );
}

function Achievements({ badges }: any) {
  if (!badges.length) return null;
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="badges-list">
      {badges.map((b: any) => (
        <Link key={b.id} to={`/daily/${b.contest_id}`} className="chip chip-accent shrink-0 py-1.5" data-testid="badge-item" title={b.title}>
          <Trophy size={12} />{b.title.replace("Vincitore UNSEEN Daily · ", "")} · {b.day}
        </Link>
      ))}
    </div>
  );
}

function Meta({ p }: any) {
  const item = (n: number, l: string, id: string) => <span data-testid={id}><b className="text-foreground font-semibold">{n}</b> {l}</span>;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink/62">
      {item(p.content_count, p.content_count === 1 ? "contenuto" : "contenuti", "profile-content-count")}
      {item(p.follower_count, "follower", "profile-follower-count")}
      {item(p.connection_count, p.connection_count === 1 ? "connessione" : "connessioni", "profile-connection-count")}
      {p.badges.length > 0 && <span className="text-primary" data-testid="profile-wins-count"><b>{p.badges.length}</b> {p.badges.length === 1 ? "vittoria" : "vittorie"}</span>}
      {p.thanks_count > 0 && <span data-testid="profile-thanks-count"><b className="text-foreground font-semibold">{p.thanks_count}</b> grazie per l'ascolto</span>}
    </div>
  );
}

export default function Profilo() {
  const { username } = useParams();
  const { user } = useAuth();
  const target = username || user.username;
  const own = target === user.username;
  const [tab, setTab] = useState("opere");
  const [rel, setRel] = useState<any>(null);
  useEffect(() => { setRel(null); setTab("opere"); }, [target]);
  const { data: p, isLoading, error, refetch } = useQuery({ queryKey: ["profile", target], queryFn: () => api.get(`/users/${target}`).then((r) => r.data), retry: false });
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const { data: saved } = useQuery({ queryKey: ["saved"], queryFn: () => api.get("/me/saved").then((r) => r.data), enabled: own && tab === "salvati" });
  const { data: mine } = useQuery({ queryKey: ["mine-all"], queryFn: () => api.get("/me/contents").then((r) => r.data), enabled: own && tab === "bozze" });
  if (isLoading) return <div className="p-10"><Spinner /></div>;
  if (error || !p) return <div className="p-6 space-y-4"><BackButton /><Empty testId="profile-not-found">Profilo non trovato.</Empty></div>;
  const relation = rel || p.relation;
  const catName = (cats || []).find((c: any) => c.slug === p.niche)?.name || p.niche;
  const block = async () => {
    try { relation.blocked ? await api.delete(`/users/${p.id}/block`) : await api.post(`/users/${p.id}/block`); toast.success(relation.blocked ? "Utente sbloccato" : "Utente bloccato"); setRel(null); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const share = async () => {
    const url = `${window.location.origin}/u/${p.username}`;
    try { if (navigator.share) await navigator.share({ title: p.display_name, url }); else { await navigator.clipboard.writeText(url); toast.success("Link al profilo copiato"); } } catch { /* annullato */ }
  };
  const tabs = [["opere", "Opere"], ...(own ? [["salvati", "Salvati"], ["bozze", "Bozze"]] : [])];
  const drafts = (mine || []).filter((c: any) => c.status !== "published");
  return (
    <div data-testid="profile-page" className="max-w-5xl md:mx-auto">
      <div className="flex items-center justify-between px-5 md:px-10 pt-5 pb-2">
        {own ? <span className="text-sm text-ink/62">Il tuo spazio</span> : <BackButton />}
        <div className="flex gap-2">
          <button onClick={share} aria-label="Condividi profilo" className="w-10 h-10 rounded-full bg-white/45 border border-ink/10 flex items-center justify-center hover:bg-white/60" data-testid="profile-share-btn"><Share2 size={16} /></button>
          {own ? (
            <>
              {user.role === "admin" && <Link to="/admin" aria-label="Admin" className="w-10 h-10 rounded-full bg-white/45 border border-ink/10 flex items-center justify-center hover:bg-white/60" data-testid="admin-link"><Shield size={16} /></Link>}
              <Link to="/impostazioni" aria-label="Impostazioni" className="w-10 h-10 rounded-full bg-white/45 border border-ink/10 flex items-center justify-center hover:bg-white/60" data-testid="settings-link"><Settings size={16} /></Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button className="w-10 h-10 rounded-full bg-white/45 border border-ink/10 flex items-center justify-center hover:bg-white/60" aria-label="Altro" data-testid="profile-more-btn"><MoreHorizontal size={16} /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-ink/10 rounded-2xl">
                <DropdownMenuItem onClick={block} data-testid="block-user-btn"><Ban size={14} className="mr-2" />{relation.blocked ? "Sblocca" : "Blocca"}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {own && !user.email_verified && <VerifyBanner />}
      <header className="px-5 md:px-10 pt-2 pb-6 space-y-4 rise">
        <div className="flex items-center gap-4">
          <Avatar user={p} size={76} ring />
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl sm:text-2xl font-semibold leading-tight truncate" data-testid="profile-display-name">{p.display_name}</h1>
            <div className="text-sm text-ink/62 truncate" data-testid="profile-username">@{p.username}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {catName && <span className="chip chip-accent" data-testid="profile-niche">{catName}</span>}
              {p.is_demo && <DemoChip />}
            </div>
          </div>
        </div>
        {p.bio && <p className="text-[15px] text-ink/84 max-w-[52ch] whitespace-pre-line leading-relaxed" data-testid="profile-bio">{p.bio}</p>}
        {p.links?.length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1">{p.links.map((l: string) => <a key={l} href={l} target="_blank" rel="noopener noreferrer nofollow" className="text-sm text-primary inline-flex items-center gap-1 hover:underline"><Link2 size={13} />{l.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>)}</div>}
        <Meta p={p} />
        <div className="flex gap-2 flex-wrap items-start">
          {own ? (
            <Link to="/impostazioni" className="btn btn-ghost px-5 py-2 text-sm" data-testid="edit-profile-btn">Modifica profilo</Link>
          ) : !relation.blocked && (<>
            <BussaButton userId={p.id} relation={relation} onChange={setRel} />
            <FollowButton userId={p.id} relation={relation} onChange={setRel} />
            {!p.is_demo && <MessageButton userId={p.id} />}
          </>)}
          {!own && relation.blocked && <span className="text-sm text-ink/62">Hai bloccato questo utente.</span>}
        </div>
        <Achievements badges={p.badges} />
      </header>
      <div className="px-5 md:px-10 space-y-8 pb-10">
        <Featured items={p.featured} />
        <div className="flex gap-1 p-1 rounded-full bg-white/[.04] border border-ink/10 w-fit" role="tablist">
          {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} role="tab" aria-selected={tab === k} data-testid={`profile-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === k ? "bg-white text-black" : "text-ink/70 hover:text-foreground"}`}>{l}</button>)}
        </div>
        {tab === "opere" && <Works items={p.contents} niche={p.niche} />}
        {tab === "salvati" && (saved?.length ? <Works items={saved} /> : <Empty>Nessun contenuto salvato.</Empty>)}
        {tab === "bozze" && (drafts.length ? (
          <div className="grid grid-cols-3 gap-1.5" data-testid="drafts-status-grid">{drafts.map((c: any) => (
            <Link key={c.id} to={c.status === "draft" ? `/pubblica?draft=${c.id}` : `/c/${c.id}`} className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-white/45">
              {src(c) && <img src={src(c)} alt="" className="w-full h-full object-cover opacity-70" />}
              <span className="absolute bottom-2 left-2 chip bg-black/60 text-white border-white/20">{{ draft: "Bozza", processing: "In elaborazione", removed: "Rimosso" }[c.status as string] || c.status}</span>
            </Link>))}</div>
        ) : <Empty>Nessuna bozza o contenuto in elaborazione.</Empty>)}
        {own && <p className="text-xs text-ink/50">Per mettere in evidenza o fissare un contenuto, aprilo e usa il menu ⋯.</p>}
      </div>
    </div>
  );
}
